import { PermissionKey } from '../utils/permission';

export type MenuSectionDef = {
  id: string; // AccordionItem key
  title: string; // 표시용
  requiredPermissions: PermissionKey[]; // 권한 키
  items: Array<{ label: string; href: string; requiredPermissions?: PermissionKey[] }>;
};

/**
 * 사이드바 메뉴 섹션 정의
 * 메뉴 추가/삭제/수정은 이 배열만 수정하면 됨
 */
export const MENU_SECTIONS: MenuSectionDef[] = [
  {
    id: 'user',
    title: '회원 관리',
    requiredPermissions: [PermissionKey.USER_MANAGEMENT],
    items: [{ label: '모든 회원 관리', href: '/user' }],
  },
  {
    id: 'problem-solved',
    title: '문제 관리',
    requiredPermissions: [PermissionKey.PROBLEM_SOLVED_MANAGEMENT],
    items: [
      { label: '대시보드', href: '/problem-solving-sessions/dashboard' },
      { label: '문제 관리', href: '/problem-solved' },
      { label: '실전 모의고사 관리', href: '/exam-round' },
      { label: '문제 풀이 세션 관리', href: '/problem-solving-sessions' },
      { label: '문제 오류 제보 관리', href: '/problem-solved/errors' },
    ],
  },
  {
    id: 'community',
    title: '커뮤니티 관리',
    requiredPermissions: [PermissionKey.COMMUNITY_MANAGEMENT],
    items: [
      { label: '카테고리 관리', href: '/community/category' },
      { label: '게시글 관리', href: '/community/post' },
      { label: '공지사항 관리', href: '/community/notice' },
      { label: 'FAQ 관리', href: '/community/faq' },
      { label: '1:1 문의 관리', href: '/community/inquiry' },
      { label: '뱃지 신청 관리', href: '/community/badge-application' },
    ],
  },
  {
    id: 'community-bot',
    title: '커뮤니티 봇 관리',
    requiredPermissions: [PermissionKey.COMMUNITY_MANAGEMENT],
    items: [
      { label: 'Topic 관리', href: '/community-bot/topic' },
      { label: 'Bot Policy 관리', href: '/community-bot/bot-policy' },
      { label: 'Persona 관리', href: '/community-bot/persona' },
      { label: 'Prompt Template 관리', href: '/community-bot/prompt-template' },
      { label: 'Bot Guardrail Rule 관리', href: '/community-bot/bot-guardrail-rule' },
      { label: 'Bot Guardrail 로그', href: '/community-bot/bot-guardrail-log' },
      { label: 'Bot Log/통계', href: '/community-bot/bot-log' },
    ],
  },
  {
    id: 'admission',
    title: '입시 관리',
    requiredPermissions: [PermissionKey.ADMISSION_MANAGEMENT],
    items: [
      { label: '시험 후기 관리', href: '/subject-review' },
      { label: '선생님 신청 관리', href: '/tutor-application' },
      { label: '과외 목록 관리', href: '/tutor' },
      { label: '합격 수기 신청 관리', href: '/pass-essay-application' },
      { label: '합격 수기 목록 관리', href: '/pass-essay' },
    ],
  },
  {
    id: 'statistics',
    title: '통계',
    requiredPermissions: [PermissionKey.STATISTICS],
    items: [
      { label: '대학 지원 통계', href: '/statistics/user-admissions' },
      {
        label: '엑셀 업로드 로그',
        href: '/statistics/user-admissions/upload-logs',
        requiredPermissions: [PermissionKey.STATISTICS_LOGS],
      },
      {
        label: '엑셀 다운로드 로그',
        href: '/statistics/user-admissions/download-logs',
        requiredPermissions: [PermissionKey.STATISTICS_LOGS],
      },
    ],
  },
  {
    id: 'sanction',
    title: '제재 관리',
    requiredPermissions: [PermissionKey.SANCTION_MANAGEMENT],
    items: [
      { label: '신고 관리', href: '/sanction/report' },
      { label: '제한 관리(처벌)', href: '/sanction/restrict' },
    ],
  },
  {
    id: 'database',
    title: '데이터베이스 관리',
    requiredPermissions: [PermissionKey.DATABASE_MANAGEMENT],
    items: [
      { label: '상품 관리', href: '/database/item' },
      { label: '대학교 관리', href: '/database/university' },
      { label: '고등학교 관리', href: '/database/highschool' },
      { label: '국가 관리', href: '/database/country' },
      { label: '표준화 과목 관리', href: '/database/standardizedSubject' },
      { label: '학과 계열 관리', href: '/database/statisticsMajorCategory' },
      { label: '뱃지 관리', href: '/database/badge' },
      { label: '모의 지원 인원 관리', href: '/database/admissionUserCount' },
    ],
  },
  {
    id: 'point',
    title: '포인트 관리',
    requiredPermissions: [PermissionKey.POINT_MANAGEMENT],
    items: [
      { label: '유저 상품 관리', href: '/point/user-item' },
      { label: '사용자 결제 이력 관리', href: '/point/payment-history' },
      { label: '포인트 이력 관리', href: '/point/point-history' },
    ],
  },
  {
    id: 'mock-applicant',
    title: '모의 지원자 관리',
    requiredPermissions: [PermissionKey.MOCK_APPLICANT_MANAGEMENT],
    items: [
      { label: '모의 지원자 관리', href: '/mock/applicant' },
      { label: '모의지원 가데이터 관리', href: '/mock/fakeApplicant' },
      { label: '모의지원 지원자 수 관리', href: '/mock/apply-count' },
    ],
  },
  {
    id: 'simulated-application',
    title: '모의지원 관리',
    requiredPermissions: [PermissionKey.SIMULATED_APPLICATION_MANAGEMENT],
    items: [
      { label: '전형 생성', href: '/admission/simulatedApplication' },
      { label: '모집단위 관리', href: '/admission/admission' },
      { label: '경쟁단위 관리', href: '/admission/competitiveUnit' },
      { label: '경쟁률 현황', href: '/simple-admission-admin/competitions' },
      { label: '경쟁률 지원자', href: '/simple-admission-admin/applications' },
      { label: '공유 이력', href: '/simple-admission-admin/share-attempts' },
    ],
  },
  {
    id: 'system',
    title: '시스템 관리',
    requiredPermissions: [PermissionKey.SYSTEM_MANAGEMENT],
    items: [
      { label: '앱 푸시 관리', href: '/system/app-push' },
      { label: '시스템 설정', href: '/system/setting' },
    ],
  },
];
